import {
  Glide,
  Overlay,
  ThemeProps,
  YoutubeThumbnail,
  styled,
} from '@teko/ds-desktop';
import {
  Gallery,
  ProductVideo,
  SOURCE_VIDEO,
  SmallImage,
} from '@teko/teko-ecommerce';
import { getTrackingData } from '@teko/tracking';
import { InlineBanner } from 'components/InlineBanner';
import { INLINE_BANNER_CLASSNAME } from 'constants/smartBanner';
import { useProductDetailState } from 'contexts';
import React, { useRef, useState } from 'react';
import { ProductImage, Video } from 'types';
import ImageZoomStyled from '../../DetailPageSGC/ProductImageZoom';

export const NewProductImages: React.FC = () => {
  const { product, frame, inlineBanners } = useProductDetailState();
  const [showGallery, setShowGallery] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const vidRef = useRef<HTMLVideoElement>(null);

  const images = product?.images || [];
  const videos = product?.videos || [];

  const displayItems = [...videos, ...images];

  const renderDisplayItems = () => {
    return displayItems.map((img, index) => {
      if (index < videos.length) {
        if ((img as Video).source === SOURCE_VIDEO.SYSTEM) {
          return (
            <StyledItemFrame key={index}>
              <ThumbnailVideo
                url={(img as Video).url}
                source={(img as Video).source}
                isActive={index === activeIndex}
                key={index}
                videoProps={{
                  ref: vidRef,
                  width: '100%',
                  height: '100%',
                  preload: 'none',
                  playsInline: true,
                  autoPlay: true,
                  muted: true,
                  onClick: () => setActiveIndex(index),
                  onPlayingCapture: () => vidRef.current?.pause(),
                }}
              />
            </StyledItemFrame>
          );
        }
        return (
          <StyledItemFrame key={index}>
            <StyledYoutubeThumbnail
              key={index}
              width="100%"
              height="100%"
              isActive={index === activeIndex}
              youtubeLink={(img as Video).url}
              onClick={() => {
                setActiveIndex(index);
              }}
            />
          </StyledItemFrame>
        );
      }
      return (
        <StyledItemFrame key={index}>
          <SmallImage
            width="100%"
            height="100%"
            borderRadius={4}
            borderColor="#dddddd"
            key={index}
            image={img as ProductImage}
            alt={(img as ProductImage).alt}
            isActive={index === activeIndex}
            borderWidth={index === activeIndex ? 1.5 : 1}
            onClick={() => {
              setActiveIndex(index);
            }}
          />
        </StyledItemFrame>
      );
    });
  };

  return (
    <div
      {...getTrackingData({
        isTrackContent: true,
        regionName: 'imgProductDetail',
        contentName: 'viewImage',
        target: 'productImage',
        payload: JSON.stringify({
          sku: product?.sellerSku,
          screenName: 'productDetail',
          index: activeIndex,
        }),
      })}
    >
      <StyledBigImages
        className="productDetailPreview" // class for pushdy banner
      >
        {frame && <FrameWrapper frame={frame} />}
        {images || videos ? (
          <>
            {!!inlineBanners && (
              <InlineBanner
                inlineBanners={inlineBanners}
                inlineBannerClass={
                  INLINE_BANNER_CLASSNAME.INLINE_PDP_PREVIEW_DESK
                }
              />
            )}
            <ImageOrVideoWrapper>
              {activeIndex < videos.length ? (
                <ProductVideo
                  source={videos[activeIndex].source}
                  videoProps={{
                    preload: 'none',
                    width: '100%',
                    height: 388,
                    playsInline: true,
                    muted: true,
                    autoPlay: true,
                    controls: true,
                    src: videos[activeIndex].url,
                    onClick: () => setShowGallery(true),
                  }}
                  youtubeFrameProps={{
                    width: '100%',
                    height: 388,
                    autoplay: 1,
                    muted: true,
                  }}
                  url={videos[activeIndex].url}
                />
              ) : (
                <ImageZoomStyled
                  onClick={() => setShowGallery(true)}
                  image={images[activeIndex - videos.length]}
                  height={388}
                />
              )}
            </ImageOrVideoWrapper>
          </>
        ) : null}
      </StyledBigImages>
      <StyledGlide
        bound
        gap={8}
        perView={5}
        {...(displayItems.length <= 4 && {
          showControl: false,
          dragThreshold: false,
          swipeThreshold: false,
        })}
        displayNewLayout={true}
      >
        {renderDisplayItems()}
      </StyledGlide>
      <Overlay show={showGallery} onEscPressed={() => setShowGallery(false)}>
        {images ? (
          <Gallery
            images={images}
            videos={videos?.map((video) => {
              return { ...video, src: video.url };
            })}
            bgColor="white"
            onClose={() => setShowGallery(false)}
            activeImageIndex={activeIndex}
          />
        ) : null}
      </Overlay>
    </div>
  );
};

interface FrameWrapperStyledProps {
  frame: string;
}

interface YoutubeThumbnailStyledProps extends ThemeProps {
  isActive: boolean;
}

const FrameWrapper = styled.div<FrameWrapperStyledProps>(
  ({ frame }: FrameWrapperStyledProps) => `
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  display: flex;
  flex-wrap: wrap;
  background-image: url(${frame});
  background-size: cover;
  height: 100%;
  width: 100%;
  cursor: pointer;
  `,
);

const StyledBigImages = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-bottom: 8px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  background-color: #f5f5f5;
`;

const StyledYoutubeThumbnail = styled(
  YoutubeThumbnail,
)<YoutubeThumbnailStyledProps>(
  ({ isActive, theme }) => `
  border: 1.5px solid
    ${isActive ? theme?.colors.primary500 : '#dddddd'};
`,
);

const ThumbnailVideo = styled(ProductVideo)<YoutubeThumbnailStyledProps>(
  ({ isActive, theme }) => `
  cursor: pointer;
  border: 1.5px solid
    ${isActive ? theme?.colors.primary500 : '#dddddd'};
  transition: 0.1s ease opacity;
  &:hover {
    opacity: 0.6;
  }
`,
);

const StyledItemFrame = styled.div`
  aspect-ratio: 1;
  min-width: 90px;
  min-height: 90px;
`;

const StyledGlide = styled(Glide)`
  .horizontal-slider,
  .glide__track {
    // 90px là chiều dầi 1 slide (tối đa 5 silide) - 8px là chiều dài khoảng cách giữa các slide (có 4 đoạn gap)
    width: ${90 * 5 + 8 * 4}px;
  }
  .glide__slide {
    width: 90px;
  }
`;

const ImageOrVideoWrapper = styled.div`
  height: 388px;
  width: 100%;
`;
